import React from "react";
import Seo from "@/components/vlyt/Seo";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { VLYT } from "@/constants/testIds/vlyt";
import { submitContact } from "@/lib/api";

export default function Contact() {
  const [form, setForm] = React.useState({ name: "", email: "", topic: "general", message: "" });
  const [loading, setLoading] = React.useState(false);
  const [ok, setOk] = React.useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitContact(form);
      setOk(true);
      toast.success("Message sent.", { description: "We'll reply within 1 business day." });
    } catch {
      toast.error("Could not send your message. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo title="Contact · Vlyt" canonical="https://govlyt.com/support/contact" />
      <section className="pt-[120px] pb-24 mx-auto max-w-[1120px] px-5 md:px-10 grid lg:grid-cols-[1.1fr_1fr] gap-14">
        <div>
          <div className="text-eyebrow mb-4">Contact</div>
          <h1 className="text-h1 font-display">
            Say <span className="text-lime">hello</span>, or send us a hard question.
          </h1>
          <p className="mt-6 text-[color:var(--vlyt-muted)] max-w-lg">
            Whether you&rsquo;re a student, driver, investor, or press: we read everything.
          </p>
          <div className="mt-10 space-y-3 text-[14px]">
            <div><span className="text-[color:var(--vlyt-muted)] font-mono uppercase text-[11px] tracking-widest">Email</span> · hello@govlyt.com</div>
            <div><span className="text-[color:var(--vlyt-muted)] font-mono uppercase text-[11px] tracking-widest">Where</span> · Port Harcourt, Rivers State</div>
            <div><span className="text-[color:var(--vlyt-muted)] font-mono uppercase text-[11px] tracking-widest">Hours</span> · Mon to Sat, 8am to 8pm WAT</div>
          </div>
        </div>

        <div className="rounded-[20px] border border-[color:var(--vlyt-border)] bg-[color:var(--vlyt-surface-1)] p-6 md:p-8">
          {ok ? (
            <div data-testid={VLYT.contact.success} className="text-center py-8">
              <div className="mx-auto h-14 w-14 rounded-full bg-[rgba(191,255,0,0.08)] flex items-center justify-center ring-1 ring-lime mb-5">
                <CheckCircle2 size={24} className="text-lime" />
              </div>
              <div className="text-h3 font-display">Message received.</div>
              <p className="text-[color:var(--vlyt-muted)] mt-2">Check your inbox. We sent a confirmation to {form.email}.</p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid={VLYT.contact.form} className="space-y-5">
              <div className="space-y-2">
                <Label className="text-[color:var(--vlyt-muted)] font-mono text-[11px] uppercase tracking-widest">Name</Label>
                <Input
                  required
                  value={form.name}
                  onChange={onChange("name")}
                  placeholder="Your full name"
                  data-testid={VLYT.contact.name}
                  className="bg-[color:var(--vlyt-surface-2)] border-[color:var(--vlyt-border)] h-11"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[color:var(--vlyt-muted)] font-mono text-[11px] uppercase tracking-widest">Email</Label>
                <Input
                  required
                  type="email"
                  value={form.email}
                  onChange={onChange("email")}
                  placeholder="you@example.com"
                  data-testid={VLYT.contact.email}
                  className="bg-[color:var(--vlyt-surface-2)] border-[color:var(--vlyt-border)] h-11"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[color:var(--vlyt-muted)] font-mono text-[11px] uppercase tracking-widest">Topic</Label>
                <Select value={form.topic} onValueChange={(v) => setForm((f) => ({ ...f, topic: v }))}>
                  <SelectTrigger
                    data-testid={VLYT.contact.topic}
                    className="bg-[color:var(--vlyt-surface-2)] border-[color:var(--vlyt-border)] h-11"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="rider">Rider support</SelectItem>
                    <SelectItem value="driver">Driver support</SelectItem>
                    <SelectItem value="press">Press / Media</SelectItem>
                    <SelectItem value="partnership">Partnership</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-[color:var(--vlyt-muted)] font-mono text-[11px] uppercase tracking-widest">Message</Label>
                <Textarea
                  required
                  rows={6}
                  value={form.message}
                  onChange={onChange("message")}
                  placeholder="Tell us what's on your mind…"
                  data-testid={VLYT.contact.message}
                  className="bg-[color:var(--vlyt-surface-2)] border-[color:var(--vlyt-border)]"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid={VLYT.contact.submit}
                className="btn-lime w-full justify-center h-12 disabled:opacity-60"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                {loading ? "Sending…" : "Send message"}
              </button>
            </form>
          )}
        </div>
      </section>
    </>
  );
}
